/* ==========================================================================
   sr-home.js — homepage behaviour for the Shopify build.

   The page script of saraura-site/index.html, unchanged except where a
   standalone page could assume things a theme cannot. Every one of those
   places is marked SHOPIFY. Progressive enhancement only: every section is
   already usable before this file runs.

   Loaded with defer, after assets/sr.js, which is where the shared behaviour
   lives (reveal-on-scroll, the card image swap on a rail, the newsletter).
   ========================================================================== */
/* ==========================================================================
   index.html — page behaviour. Progressive enhancement only: every section
   below is already usable before this file runs.
     1. Pointer-drag with momentum on the selection rail (fine pointers).
     2. Pinned horizontal lookbook (wide, fine-pointer, motion allowed).
     3. Close a drawer when a link inside it is followed.
     4. Newsletter form — answers with a way to reach a person.
   ========================================================================== */
(function () {
  'use strict';

  var doc = document, root = doc.documentElement;
  var mqMotion = matchMedia('(prefers-reduced-motion: reduce)');
  var mqFine   = matchMedia('(hover: hover) and (pointer: fine)');
  var mqWide   = matchMedia('(min-width: 62em)');

  /* --- 1. Drag rail ----------------------------------------------------- */
  function initDragRail(rail) {
    if (!rail || !mqFine.matches || !window.PointerEvent) return;

    var down = false, pid = null, moved = false;
    var startX = 0, startLeft = 0, lastX = 0, lastT = 0, vel = 0;
    var raf = 0, blockUntil = 0;

    rail.classList.add('is-drag');

    function stop() { if (raf) { cancelAnimationFrame(raf); raf = 0; } }

    function glide() {
      raf = 0;
      vel *= 0.94;
      if (Math.abs(vel) < 0.02) return;
      var before = rail.scrollLeft;
      rail.scrollLeft = before - vel * 16;
      if (Math.abs(rail.scrollLeft - before) < 0.5) return;   // hit an end
      raf = requestAnimationFrame(glide);
    }

    rail.addEventListener('pointerdown', function (e) {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      stop();
      down = true; moved = false; pid = e.pointerId;
      startX = lastX = e.clientX;
      startLeft = rail.scrollLeft;
      lastT = e.timeStamp; vel = 0;
    });

    rail.addEventListener('pointermove', function (e) {
      if (!down || e.pointerId !== pid) return;
      var dx = e.clientX - startX;
      if (!moved) {
        if (Math.abs(dx) < 4) return;               // let a plain click be a click
        moved = true;
        rail.classList.add('is-dragging');
        try { rail.setPointerCapture(pid); } catch (err) {}
      }
      rail.scrollLeft = startLeft - dx;
      var dt = e.timeStamp - lastT;
      if (dt > 0) {
        vel = 0.75 * ((e.clientX - lastX) / dt) + 0.25 * vel;
        lastX = e.clientX;
        lastT = e.timeStamp;
      }
      e.preventDefault();
    });

    function release() {
      if (!down) return;
      down = false;
      rail.classList.remove('is-dragging');
      try { if (pid !== null) rail.releasePointerCapture(pid); } catch (err) {}
      pid = null;
      if (moved) { blockUntil = Date.now() + 250; stop(); raf = requestAnimationFrame(glide); }
    }
    rail.addEventListener('pointerup', release);
    rail.addEventListener('pointercancel', release);
    rail.addEventListener('lostpointercapture', release);

    // A drag that ends on a card must not follow the card's link.
    rail.addEventListener('click', function (e) {
      if (Date.now() < blockUntil) { e.preventDefault(); e.stopPropagation(); }
    }, true);

    rail.addEventListener('dragstart', function (e) { if (moved) e.preventDefault(); });
    rail.addEventListener('wheel', stop, { passive: true });
  }

  /* --- 2. Pinned lookbook ------------------------------------------------
     Vertical scroll distance maps 1:1 onto sideways travel, which is what
     lets keyboard focus be translated straight back into a scroll position. */
  function initLookbook(sec) {
    if (!sec) return;
    var outer  = sec.querySelector('.lb__outer');
    var sticky = sec.querySelector('.lb__sticky');
    var rail   = sec.querySelector('.lb__rail');
    var track  = sec.querySelector('.lb__track');
    var bar    = sec.querySelector('.lb__progress > span');
    // SHOPIFY: the standalone build's own header is not on this theme yet, so
    // there is nothing to measure. The section carries the theme header's
    // height as a setting instead, and it is used only if the real element
    // is absent — the day the Saraura header lands, the measurement wins.
    var header = doc.querySelector('.sr-header');
    var hFallback = parseFloat(sec.getAttribute('data-header-offset')) || 0;
    function headerH() { return header ? header.offsetHeight : hFallback; }
    if (!outer || !sticky || !rail || !track) return;

    var pinned = false, released = false, travel = 0, top0 = 0;
    var target = 0, current = 0, raf = 0, mTimer = 0;

    function apply() {
      track.style.setProperty('--lb-x', current.toFixed(2) + 'px');
      if (bar && travel) bar.style.setProperty('--lb-p', (-current / travel).toFixed(4));
    }

    function tick() {
      raf = 0;
      current += (target - current) * 0.16;
      if (Math.abs(target - current) < 0.4) current = target;
      apply();
      if (current !== target) raf = requestAnimationFrame(tick);
    }

    function onScroll(instant) {
      if (!pinned || !travel) return;
      var h = headerH();
      var p = (window.scrollY - (top0 - h)) / travel;
      p = p < 0 ? 0 : (p > 1 ? 1 : p);
      target = -travel * p;
      if (instant) { if (raf) { cancelAnimationFrame(raf); raf = 0; } current = target; apply(); }
      else if (!raf) raf = requestAnimationFrame(tick);
    }

    function measure() {
      if (!pinned) return;
      var viewH = sticky.getBoundingClientRect().height;
      var trackW = Math.max(track.scrollWidth, Math.ceil(track.getBoundingClientRect().width));
      travel = Math.max(0, trackW - rail.clientWidth);
      outer.style.setProperty('--lb-h', Math.round(viewH + travel) + 'px');
      top0 = outer.getBoundingClientRect().top + window.scrollY;
      onScroll(true);
    }

    function pin() {
      pinned = true;
      sec.classList.add('is-pinned');
      measure();
    }
    function unpin() {
      pinned = false;
      sec.classList.remove('is-pinned');
      outer.style.removeProperty('--lb-h');
      track.style.removeProperty('--lb-x');
      if (bar) bar.style.removeProperty('--lb-p');
      if (raf) { cancelAnimationFrame(raf); raf = 0; }
      current = target = 0;
    }

    function sync() {
      var want = mqWide.matches && mqFine.matches && !mqMotion.matches && !released;
      if (want && !pinned) pin();
      else if (!want && pinned) unpin();
      else if (want) measure();
    }

    // Keyboard access.
    // When pinned, a look's horizontal position is a transform, and the
    // browser's own scroll-into-view cannot reason about that — it races the
    // page and strands focus off the side of the screen. Rather than fight
    // it, hand the gallery back to the browser: drop to the plain scroll-snap
    // rail, which is a real scroll container it already handles correctly.
    // Mouse focus is left alone, so pinning survives an ordinary click.
    track.addEventListener('focusin', function (e) {
      if (!pinned) return;
      var t = e.target;
      var byKeyboard = false;
      try { byKeyboard = !!(t.matches && t.matches(':focus-visible')); } catch (err) {}
      if (!byKeyboard) return;
      var look = t.closest ? t.closest('.lb__look') : null;
      if (!look) return;

      var anchor = top0 - headerH();
      released = true;                 // stay a rail for the rest of the visit
      unpin();
      var prev = root.style.scrollBehavior;
      root.style.scrollBehavior = 'auto';
      window.scrollTo(0, anchor);      // the section keeps its place on screen
      if (look.scrollIntoView) look.scrollIntoView({ block: 'nearest', inline: 'center' });
      requestAnimationFrame(function () { root.style.scrollBehavior = prev; });
    });

    addEventListener('scroll', function () { onScroll(false); }, { passive: true });
    addEventListener('resize', function () {
      clearTimeout(mTimer);
      mTimer = setTimeout(sync, 150);
    }, { passive: true });
    addEventListener('load', sync);
    if (mqWide.addEventListener) {
      mqWide.addEventListener('change', sync);
      mqMotion.addEventListener('change', sync);
    }
    sync();
  }

  /* Drawer-link closing and the footer newsletter both live in assets/sr.js
     so that every page shares one implementation. */

  function boot() {
    // SHOPIFY: one homepage can carry more than one rail and more than one
    // lookbook, because both are sections the owner can add twice in the
    // theme editor. The standalone build could address them by id; here
    // they are found by class, and each one is initialised on its own.
    doc.querySelectorAll('.rail[data-sr-drag]').forEach(initDragRail);
    doc.querySelectorAll('.lb').forEach(initLookbook);
  }
  if (doc.readyState !== 'loading') boot();
  else doc.addEventListener('DOMContentLoaded', boot, { once: true });
})();
